import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, HStack, IconButton, Spinner, Table, Text, VStack } from "@chakra-ui/react";
import { HiArrowRight } from "react-icons/hi2";
import WorkflowService from "@/services/agency/WorkflowService";
import { toApiRequest } from "@/services/jobOfferMapper";
import { createEmptyJobOffer } from "@/models/jobOffer";
import { showToast } from "@/components/ui/toaster";
import CreateRecruitmentDialog from "@/pages/clients/components/CreateRecruitmentDialog";
import JobOfferFormModal from "@/components/jobOffer/JobOfferFormModal";
import { DetailTableShell, tableHeaderStyle, WorkflowStatusBadge } from "@/components/ui/detail/primitives";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("pl-PL") : "—");

const COLUMNS = ["Nazwa", "Scenariusz", "Status", "Edytowano", "Utworzono", "Akcje"];

const ClientRecruitmentsTab = ({ clientId, scenarios, workflows, isLoading, onChanged }) => {
    const navigate = useNavigate();
    const [pickerOpen, setPickerOpen] = useState(false);
    const [createScenarioId, setCreateScenarioId] = useState(null);

    const recruiterScenarios = useMemo(
        () => scenarios.filter((s) => s.type == null || s.type === "Recruiter"),
        [scenarios],
    );

    const handleCreate = async (values, isDraft = false) => {
        const created = await WorkflowService.create(clientId, toApiRequest(createScenarioId, values, { isDraft }));
        showToast("Rekrutacja utworzona", null, "success");
        setCreateScenarioId(null);
        onChanged();
        if (created?.id) navigate(`/clients/${clientId}/workflows/${created.id}`);
    };

    return (
        <VStack w="full" align="flex-start" gap={3} pt={4}>
            <HStack w="full" justify="flex-end">
                <Button size="sm" variant="outline" onClick={() => setPickerOpen(true)}>Dodaj rekrutację</Button>
            </HStack>

            {isLoading ? (
                <VStack w="full" py={8} align="center"><Spinner color="white" size="md" /></VStack>
            ) : (
                <DetailTableShell>
                    <Table.Root>
                        <Table.Header>
                            <Table.Row bg="bg.card" borderBottom="1px solid" borderColor="border.primary">
                                {COLUMNS.map((col, i) => (
                                    <Table.ColumnHeader key={col} {...tableHeaderStyle} textAlign={i === COLUMNS.length - 1 ? "right" : "left"}>
                                        {col}
                                    </Table.ColumnHeader>
                                ))}
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {workflows.length === 0 ? (
                                <Table.Row bg="bg.secondary">
                                    <Table.Cell colSpan={COLUMNS.length} py={8} border="none" textAlign="center">
                                        <Text color="text.grey" fontSize="sm">Brak rekrutacji</Text>
                                    </Table.Cell>
                                </Table.Row>
                            ) : workflows.map((w) => (
                                <Table.Row key={w.id} bg="bg.secondary" borderBottom="1px solid" borderColor="border.primary" _hover={{ bg: "bg.highlight" }} transition="background 0.15s">
                                    <Table.Cell py={4} px={5} border="none">
                                        <Text color="white" fontSize="sm" fontWeight={500}>{w.name}</Text>
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none">
                                        <Text color="text.grey" fontSize="sm">{w.scenario?.name ?? "—"}</Text>
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none">
                                        <WorkflowStatusBadge status={w.status} />
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none">
                                        <Text color="text.grey" fontSize="sm">{fmtDate(w.editedAtUtc)}</Text>
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none">
                                        <Text color="text.grey" fontSize="sm">{fmtDate(w.createdAtUtc)}</Text>
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none" textAlign="right">
                                        <IconButton variant="outlined" size="sm" aria-label="Szczegóły rekrutacji" onClick={() => navigate(`/clients/${clientId}/workflows/${w.id}`)}>
                                            <HiArrowRight />
                                        </IconButton>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table.Root>
                </DetailTableShell>
            )}

            {pickerOpen && (
                <CreateRecruitmentDialog
                    scenarios={recruiterScenarios}
                    onClose={() => setPickerOpen(false)}
                    onPick={(scenarioId) => { setPickerOpen(false); setCreateScenarioId(scenarioId); }}
                />
            )}

            {createScenarioId && (
                <JobOfferFormModal
                    onClose={() => setCreateScenarioId(null)}
                    initialValues={{ ...createEmptyJobOffer(), scenarioId: createScenarioId }}
                    onSubmit={handleCreate}
                />
            )}
        </VStack>
    );
};

export default ClientRecruitmentsTab;
